import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// === Challenge constants =====================================================
const CHALLENGE_GRAPH_ID = 5;          // Petersen Graph
const CHALLENGE_AGENT = 'AlphaQ Up';
const CHALLENGE_TOTAL_GAMES = 5;
const CHALLENGE_GAME_TRANSITION_MS = 3200;
// =============================================================================

function GraphGame({ userData, width = 900, height = 650 }) {
  const { getAccessTokenSilently, isAuthenticated, isLoading: authLoading } = useAuth0();
  const navigate = useNavigate();
  const svgRef = useRef(null);

  // Game setup states
  const [gameStarted, setGameStarted] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedGraphId, setSelectedGraphId] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [availableGraphIds, setAvailableGraphIds] = useState([]);
  const [graphsLoading, setGraphsLoading] = useState(true);

  // Game state
  const [graphData, setGraphData] = useState(null);
  const [currentTurn, setCurrentTurn] = useState('player1');
  const [edgeColors, setEdgeColors] = useState({});
  const [finalEdgeColors, setFinalEdgeColors] = useState({});
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [showColorMenu, setShowColorMenu] = useState(false);
  const [colorMenuPosition, setColorMenuPosition] = useState({ x: 0, y: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gameId, setGameId] = useState(null);
  const gameIdRef = useRef(null);
  const [influenceVector, setInfluenceVector] = useState([]);
  const [interimScore, setInterimScore] = useState(0);
  const [epsilon, setEpsilon] = useState(0);

  // === Challenge state =======================================================
  const [isChallengeMode, setIsChallengeMode] = useState(false);
  const [challengeGameNum, setChallengeGameNum] = useState(0);     // 1..5
  const [challengeResults, setChallengeResults] = useState([]);    // [{ gameNum, result, playerSide, eloBefore, eloAfter, eloChange, gameWinner }]
  const [showGameTransition, setShowGameTransition] = useState(false);
  const [lastGameResult, setLastGameResult] = useState(null);
  const [showChallengeComplete, setShowChallengeComplete] = useState(false);
  const [challengeStarting, setChallengeStarting] = useState(false);

  // Mirror challenge state in refs so cleanup handlers (unmount / beforeunload)
  // can see the latest values without re-binding listeners.
  const isChallengeModeRef = useRef(false);
  const challengeGameNumRef = useRef(0);
  const challengeResultsRef = useRef([]);
  const showChallengeCompleteRef = useRef(false);

  useEffect(() => { isChallengeModeRef.current = isChallengeMode; }, [isChallengeMode]);
  useEffect(() => { challengeGameNumRef.current = challengeGameNum; }, [challengeGameNum]);
  useEffect(() => { challengeResultsRef.current = challengeResults; }, [challengeResults]);
  useEffect(() => { showChallengeCompleteRef.current = showChallengeComplete; }, [showChallengeComplete]);
  // ===========================================================================

  const graphNameMap = new Map([
    [11, "2 Edges: Path graph on 3 vertices"],
    [2, "3 Edges: Complete graph on 3 vertices"],
    [20, "5 Edges: Diamond Graph"],
    [19, "7 Edges: Barbell Graph"],
    [18, "9 Edges: 3-Prism"],
    [12, "11 Edges: Moser Spindle"],
    [5, "15 Edges: Petersen Graph"]
  ]);

  const epsilonMap = new Map([
    [2, 0.5],
    [5, 0.0005],
    [11, 0.5],
    [12, 0.05],
    [18, 0.125],
    [19, 0.25],
    [20, 0.5]
  ]);

  const MAX_SCORE_DISPLAY = 1.0;

  // Check if user needs to set username
  const needsUsername = isAuthenticated && userData && !userData.username;

  // Helper function to get the player's display name with fallbacks
  const getPlayerName = () => {
    return userData?.username || userData?.name || userData?.email || 'Unknown Player';
  };

  const colorToInt = (color) => {
    const mapping = { 'grey': 1, 'green': 2, 'purple': 3 };
    return mapping[color] || 0;
  };

  const intToColor = (intValue) => {
    const mapping = { 1: 'grey', 2: 'green', 3: 'purple' };
    return mapping[intValue] || 'uncolored';
  };

  const getAiOpponentId = (agentName) => {
    const aiAgentIds = {
      'Random Randy': 3,
      'MCTS Murray': 4,
      'MCTS Melissa': 5,
      'AlphaZero Andy': 6,
      'AlphaQ Up': 30
    };
    return aiAgentIds[agentName];
  };

  // Returns the parsed response data (or null) so callers can use ELO info.
  // The `silent` flag suppresses the per-game alert (used in challenge mode).
  const recordGameCompletion = async (gameWinner, aiOpponentId, silent = false) => {
    try {
      const token = await getAccessTokenSilently();

      let result;
      if (gameWinner === 'draw') {
        result = 'draw';
      } else {
        const humanWon =
          (selectedPlayer === 'player1' && gameWinner === 'red') ||
          (selectedPlayer === 'player2' && gameWinner === 'blue');
        result = humanWon ? 'win' : 'loss';
      }

      const response = await fetch(`${API_BASE_URL}/api/games/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          player_id: userData?.id,
          ai_opponent_id: aiOpponentId,
          graph_id: typeof selectedGraphId === 'string' ? parseInt(selectedGraphId) : selectedGraphId,
          result: result,
          game_id: gameIdRef.current
        })
      });

      if (!response.ok) throw new Error('Failed to record game');

      const data = await response.json();
      console.log('Game recorded:', data);

      if (!silent) {
        alert(`Game recorded! Your ELO: ${data.player_elo_before} → ${data.player_elo_after} (${data.player_elo_change >= 0 ? '+' : ''}${data.player_elo_change})`);
      }

      return { ...data, result };
    } catch (error) {
      console.error('Error recording game:', error);
      return null;
    }
  };

  useEffect(() => {
    if (!needsUsername) {
      void fetchAvailableGraphs();
    }
  }, [needsUsername]);

  // Clear agent selection if it becomes invalid due to graph selection
  useEffect(() => {
    if (!selectedGraphId) return;
    const graphId = parseInt(selectedGraphId);
    const isLargeGraph = graphId === 5 || graphId === 12;
    if (isLargeGraph && selectedAgent === 'AlphaZero Andy') {
      setSelectedAgent('');
    }
    if (!isLargeGraph && selectedAgent === 'AlphaZero Amara') {
      setSelectedAgent('');
    }
  }, [selectedGraphId]);

  // Clear graph selection if it becomes invalid due to agent selection
  useEffect(() => {
    if (!selectedAgent || !selectedGraphId) return;
    const graphId = parseInt(selectedGraphId);
    const isLargeGraph = graphId === 5 || graphId === 12;
    if (selectedAgent === 'AlphaZero Andy' && isLargeGraph) {
      setSelectedGraphId('');
    }
    if (selectedAgent === 'AlphaZero Amara' && !isLargeGraph) {
      setSelectedGraphId('');
    }
  }, [selectedAgent]);

  const fetchAvailableGraphs = async () => {
    setGraphsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/graphs/list`);
      if (!response.ok) throw new Error('Failed to fetch graph list');

      const data = await response.json();
      setAvailableGraphIds(data.predefined_graphs || []);

      if (data.predefined_graphs && data.predefined_graphs.length === 1) {
        setSelectedGraphId(data.predefined_graphs[0].toString());
      }
    } catch (error) {
      console.error('Error fetching available graphs:', error);
      setAvailableGraphIds([1, 2, 3, 4, 5]);
    } finally {
      setGraphsLoading(false);
    }
  };

  const fetchGraphData = async (graphId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/graph/${graphId}`);
      if (!response.ok) throw new Error('Failed to fetch graph data');
      return await response.json();
    } catch (error) {
      console.error('Error fetching graph data:', error);
      return {
        num_nodes: 6,
        edge_list: [[0,1],[0,2],[1,2],[1,3],[2,3],[2,4],[3,4],[3,5],[4,5]],
        vertex_positions: [[0,0],[1,0],[0.5,0.866],[1.5,0.866],[0,1.732],[1,1.732]],
        player1_node: 0,
        player2_node: 5
      };
    }
  };

  // Shared between normal start and challenge start.
  const initializeBackendGame = async (graphId, playerSide, graphInfo) => {
    const token = await getAccessTokenSilently();
    const playerName = getPlayerName();
    const player1Name = playerSide === 'player1' ? playerName : (isChallengeModeRef.current ? CHALLENGE_AGENT : selectedAgent);
    const player2Name = playerSide === 'player1' ? (isChallengeModeRef.current ? CHALLENGE_AGENT : selectedAgent) : playerName;

    const response = await fetch(`${API_BASE_URL}/api/initialize_game`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        num_vertices: graphInfo.num_nodes,
        edges: graphInfo.edge_list,
        player1_invite: player1Name,
        player2_invite: player2Name,
        graph_id: graphId,
        vertex_ownership: [graphInfo.player1_node, graphInfo.player2_node]
      })
    });

    if (!response.ok) throw new Error('Failed to initialize game');
    return response.json();
  };

  const handleStartGame = async () => {
    if (!selectedPlayer || !selectedGraphId || !selectedAgent) {
      alert('Please select all options before starting the game');
      return;
    }

    setEpsilon(epsilonMap.get(parseInt(selectedGraphId)) || 0);
    setIsLoading(true);

    try {
      const graphInfo = await fetchGraphData(selectedGraphId);
      const data = await initializeBackendGame(parseInt(selectedGraphId), selectedPlayer, graphInfo);

      setGameId(data.game_id);
      gameIdRef.current = data.game_id;

      setGraphData(graphInfo);
      setCurrentTurn('player1');
      setEdgeColors({});
      setFinalEdgeColors({});
      setGameStarted(true);
    } catch (error) {
      // BUGFIX: alert() only takes one argument; the original swallowed the error message.
      alert('Error starting game: ' + (error?.message || error));
    } finally {
      setIsLoading(false);
    }
  };

  // === Challenge: start ======================================================
  const startChallenge = async () => {
    if (challengeStarting) return;
    setChallengeStarting(true);

    // Reset challenge bookkeeping
    setChallengeResults([]);
    challengeResultsRef.current = [];
    setShowChallengeComplete(false);
    showChallengeCompleteRef.current = false;

    // Engage challenge mode and pin agent/graph
    setIsChallengeMode(true);
    isChallengeModeRef.current = true;
    setSelectedAgent(CHALLENGE_AGENT);
    setSelectedGraphId(CHALLENGE_GRAPH_ID.toString());
    setEpsilon(epsilonMap.get(CHALLENGE_GRAPH_ID) || 0);

    try {
      await beginChallengeGame(1);
    } catch (error) {
      console.error('Failed to start challenge:', error);
      alert('Could not start the challenge: ' + (error?.message || error));
      // Roll back
      setIsChallengeMode(false);
      isChallengeModeRef.current = false;
    } finally {
      setChallengeStarting(false);
    }
  };

  // Set up state and initialize one challenge game on the backend.
  const beginChallengeGame = async (gameNum) => {
    const playerSide = (gameNum % 2 === 1) ? 'player1' : 'player2';

    // Local resets
    setChallengeGameNum(gameNum);
    challengeGameNumRef.current = gameNum;
    setSelectedPlayer(playerSide);
    setCurrentTurn('player1');
    setEdgeColors({});
    setFinalEdgeColors({});
    setSelectedEdge(null);
    setShowColorMenu(false);
    setGameOver(false);
    setWinner(null);
    setInfluenceVector([]);
    setInterimScore(0);
    setIsLoading(true);

    try {
      const graphInfo = await fetchGraphData(CHALLENGE_GRAPH_ID);
      // initializeBackendGame uses isChallengeModeRef.current to pick the agent name.
      const data = await initializeBackendGame(CHALLENGE_GRAPH_ID, playerSide, graphInfo);

      setGameId(data.game_id);
      gameIdRef.current = data.game_id;
      setGraphData(graphInfo);
      setGameStarted(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Called from handleGameOver when in challenge mode.
  const handleChallengeGameComplete = (gameWinner, recordedData) => {
    const gameNum = challengeGameNumRef.current;
    const playerSide = gameNum % 2 === 1 ? 'player1' : 'player2';

    let playerResult;
    if (gameWinner === 'draw') {
      playerResult = 'draw';
    } else {
      const playerColor = playerSide === 'player1' ? 'red' : 'blue';
      playerResult = gameWinner === playerColor ? 'win' : 'loss';
    }

    const newEntry = {
      gameNum,
      result: playerResult,
      playerSide,
      gameWinner,
      eloBefore: recordedData?.player_elo_before ?? null,
      eloAfter: recordedData?.player_elo_after ?? null,
      eloChange: recordedData?.player_elo_change ?? 0
    };

    const updated = [...challengeResultsRef.current, newEntry];
    setChallengeResults(updated);
    challengeResultsRef.current = updated;

    setLastGameResult(playerResult);
    setShowGameTransition(true);

    // After a beat, either advance or finalize.
    window.setTimeout(async () => {
      setShowGameTransition(false);

      if (gameNum < CHALLENGE_TOTAL_GAMES) {
        try {
          await beginChallengeGame(gameNum + 1);
        } catch (error) {
          console.error('Failed to start next challenge game:', error);
          alert(`Could not start game ${gameNum + 1}: ${error?.message || error}`);
          // Bail out of challenge mode but keep recorded results.
          setShowChallengeComplete(true);
          showChallengeCompleteRef.current = true;
        }
      } else {
        // All five games done.
        setShowChallengeComplete(true);
        showChallengeCompleteRef.current = true;
      }
    }, CHALLENGE_GAME_TRANSITION_MS);
  };

  // Tally helper used by both the progress display and the final screen.
  const tallyChallenge = (results) => {
    const wins = results.filter(r => r.result === 'win').length;
    const losses = results.filter(r => r.result === 'loss').length;
    const draws = results.filter(r => r.result === 'draw').length;
    let overall;
    if (wins > losses) overall = 'player_won';
    else if (losses > wins) overall = 'ai_won';
    else overall = 'draw';
    return { wins, losses, draws, overall };
  };

  // === Forfeit (best-effort) =================================================
  // If the user leaves mid-challenge we record losses for the current
  // (uncompleted) game and every game not yet played. Each forfeit needs its
  // own game_id from /api/initialize_game.
  const forfeitRemainingGames = async () => {
    if (!isChallengeModeRef.current) return;
    if (showChallengeCompleteRef.current) return;

    const completedNums = new Set(challengeResultsRef.current.map(r => r.gameNum));
    const aiOpponentId = getAiOpponentId(CHALLENGE_AGENT);
    if (!aiOpponentId) return;

    let token;
    try {
      token = await getAccessTokenSilently();
    } catch (e) {
      console.error('Could not get token for forfeit:', e);
      return;
    }

    const currentNum = challengeGameNumRef.current;

    for (let gameNum = currentNum; gameNum <= CHALLENGE_TOTAL_GAMES; gameNum++) {
      if (completedNums.has(gameNum)) continue;

      try {
        let forfeitGameId;

        // The current game is already initialized server-side.
        if (gameNum === currentNum && gameIdRef.current) {
          forfeitGameId = gameIdRef.current;
        } else {
          // Initialize a fresh game on the backend just to record a loss against it.
          const playerSide = gameNum % 2 === 1 ? 'player1' : 'player2';
          const graphInfo = await fetchGraphData(CHALLENGE_GRAPH_ID);
          const playerName = getPlayerName();
          const player1Name = playerSide === 'player1' ? playerName : CHALLENGE_AGENT;
          const player2Name = playerSide === 'player1' ? CHALLENGE_AGENT : playerName;

          const initResp = await fetch(`${API_BASE_URL}/api/initialize_game`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              num_vertices: graphInfo.num_nodes,
              edges: graphInfo.edge_list,
              player1_invite: player1Name,
              player2_invite: player2Name,
              graph_id: CHALLENGE_GRAPH_ID,
              vertex_ownership: [graphInfo.player1_node, graphInfo.player2_node]
            })
          });
          if (!initResp.ok) continue;
          const initData = await initResp.json();
          forfeitGameId = initData.game_id;
        }

        await fetch(`${API_BASE_URL}/api/games/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            player_id: userData?.id,
            ai_opponent_id: aiOpponentId,
            graph_id: CHALLENGE_GRAPH_ID,
            result: 'loss',
            game_id: forfeitGameId
          })
        });
      } catch (error) {
        console.error(`Error forfeiting challenge game ${gameNum}:`, error);
      }
    }
  };

  // beforeunload warning + best-effort forfeit attempt. We don't rely on the
  // async forfeit completing during unload; the unmount cleanup below handles
  // the in-app navigation case more reliably.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isChallengeModeRef.current && !showChallengeCompleteRef.current) {
        // Kick off forfeit (fire-and-forget; may not complete on hard unload).
        forfeitRemainingGames();
        e.preventDefault();
        e.returnValue = 'Leaving will forfeit all remaining games in the Challenge!';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // On unmount (in-app navigation), forfeit anything outstanding.
  useEffect(() => {
    return () => {
      if (isChallengeModeRef.current && !showChallengeCompleteRef.current) {
        forfeitRemainingGames();
      }
    };
  }, []);

  const handleForfeitChallenge = async () => {
    if (!window.confirm(
      'Forfeit the Challenge?\n\nAll remaining games (including this one) will be recorded as losses.'
    )) return;

    await forfeitRemainingGames();
    setShowChallengeComplete(true);
    showChallengeCompleteRef.current = true;
  };
  // ===========================================================================

  const makeAgentMove = async () => {
    if (!graphData) return;
    if (currentTurn === selectedPlayer) return;

    const agentName = isChallengeModeRef.current ? CHALLENGE_AGENT : selectedAgent;
    const token = await getAccessTokenSilently();

    try {
      const response = await fetch(`${API_BASE_URL}/api/make_move`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          game_id: gameIdRef.current,
          player_id: agentName,
        })
      });

      if (!response.ok) {
        try {
          const error = await response.json();
          console.log(error);
        } catch (_) { /* ignore body parse failures */ }
        throw new Error('Failed to make agent move');
      }

      const data = await response.json();

      if (data.edge_index !== undefined && data.color !== undefined) {
        if (edgeColors && edgeColors[data.edge_index] !== undefined) {
          console.warn('Agent suggested a colored edge; ignoring:', data.edge_index);
        } else {
          const colorStr = typeof data.color === 'number' ? intToColor(data.color) : data.color;
          await handleEdgeColoring(data.edge_index, colorStr, false);
        }
      }
    } catch (error) {
      console.error('Error making agent move:', error);

      const uncoloredEdges = graphData.edge_list
        .map((_, idx) => idx)
        .filter(idx => edgeColors[idx] === undefined);

      if (uncoloredEdges.length > 0) {
        const randomEdge = uncoloredEdges[Math.floor(Math.random() * uncoloredEdges.length)];
        const colors = ['grey', 'green', 'purple'];
        const randomColor = colors[Math.floor(Math.random() * colors.length)];
        await handleEdgeColoring(randomEdge, randomColor, false);
      }
    }
  };

  const handleEdgeColoring = async (edgeIndex, color, isPlayerMove = true) => {
    if (!graphData) return;

    if (edgeColors && edgeColors[edgeIndex] !== undefined) {
      console.warn('Attempt to recolor edge', edgeIndex);
      return;
    }

    if (isPlayerMove) {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${API_BASE_URL}/api/make_move`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            game_id: gameIdRef.current,
            player_id: getPlayerName(),
            human_move: [0, edgeIndex, colorToInt(color)],
          })
        });

        // BUGFIX: the original swallowed backend rejections silently and updated
        // the UI as if the move had succeeded.
        if (!response.ok) {
          let detail = '';
          try { detail = JSON.stringify(await response.json()); } catch (_) {}
          console.error('Player move rejected by server:', detail);
          alert('Your move could not be registered. Please try again.');
          return;
        }
      } catch (error) {
        console.error('Error sending player move:', error);
        alert('Could not reach the server. Please try again.');
        return;
      }
    }

    const updated = { ...edgeColors, [edgeIndex]: color };
    setEdgeColors(updated);

    const updatedIntColors = { ...finalEdgeColors, [edgeIndex]: colorToInt(color) };
    setFinalEdgeColors(updatedIntColors);

    const coloredEdgeCount = Object.keys(updated).length;
    const totalEdges = graphData?.edge_list?.length ?? 0;
    const isGameOver = totalEdges > 0 && coloredEdgeCount === totalEdges;

    await fetchInfluenceVector(updatedIntColors);

    if (isGameOver) {
      setTimeout(() => {
        handleGameOver(updatedIntColors);
      }, 500);
    } else {
      setCurrentTurn(prevTurn => (prevTurn === 'player1' ? 'player2' : 'player1'));
    }

    setSelectedEdge(null);
    setShowColorMenu(false);
  };

  const handleGameOver = async (intEdgeColors) => {
    const edges = graphData.edge_list.map(([n1, n2], idx) => [
      n1, n2, intEdgeColors[idx] ?? 0,
    ]);

    const agentNameForState = isChallengeModeRef.current ? CHALLENGE_AGENT : selectedAgent;
    const playerName = getPlayerName();
    const gameState = {
      num_nodes: graphData.num_nodes,
      edges: edges,
      player1_id: selectedPlayer === 'player1' ? playerName : agentNameForState,
      player2_id: selectedPlayer === 'player1' ? agentNameForState : playerName,
      turn_count: Object.keys(intEdgeColors).length,
      current_player_index: currentTurn === 'player1' ? 1 : 2,
      player1_node: graphData.player1_node,
      player2_node: graphData.player2_node,
    };

    const graphIdForCall = isChallengeModeRef.current ? CHALLENGE_GRAPH_ID : parseInt(selectedGraphId);
    const adjudicationResult = await checkGameWinner(gameState, graphIdForCall);
    console.log("Adjudication result:", adjudicationResult);
    console.log("Final edge colors (integers):", intEdgeColors);

    let recordedData = null;
    if (adjudicationResult.winner && userData) {
      const aiOpponentId = getAiOpponentId(agentNameForState);
      if (aiOpponentId) {
        recordedData = await recordGameCompletion(
          adjudicationResult.winner,
          aiOpponentId,
          isChallengeModeRef.current   // silent inside the challenge — we show a series summary instead
        );
      }
    }

    setGameOver(true);

    if (isChallengeModeRef.current) {
      handleChallengeGameComplete(adjudicationResult.winner, recordedData);
    }
  };

  const checkGameWinner = async (gameState, graph_id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/adjudicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_state: gameState,
          graph_id: graph_id,
          // BUGFIX: use the ref to avoid sending a stale gameId from closure.
          game_id: gameIdRef.current
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const adjudicationResult = await response.json();
      // BUGFIX: removed redundant setGameOver(true) — handleGameOver already sets it.
      setWinner(adjudicationResult.winner);
      return adjudicationResult;
    } catch (error) {
      console.error('Adjudication failed:', error);

      const fallbackWinner = Math.random() > 0.5 ? 'red' : 'blue';
      setWinner(fallbackWinner);

      return {
        game_state: gameState,
        adjudicator: 'fallback',
        winner: fallbackWinner,
        score: null,
        influence_vector: null,
        correlation_matrix: null,
        parameters: {},
      };
    }
  };

  const fetchInfluenceVector = async (updatedIntColors = null) => {
    if (!graphData || !gameIdRef.current) return;

    try {
      const token = await getAccessTokenSilently();

      const colorsToUse = updatedIntColors || finalEdgeColors;

      const edges = graphData.edge_list.map(([n1, n2], idx) => [
        n1, n2, colorsToUse[idx] ?? 0,
      ]);

      const agentNameForState = isChallengeModeRef.current ? CHALLENGE_AGENT : selectedAgent;
      const playerName = getPlayerName();
      const gameState = {
        num_nodes: graphData.num_nodes,
        edges: edges,
        player1_id: selectedPlayer === 'player1' ? playerName : agentNameForState,
        player2_id: selectedPlayer === 'player1' ? agentNameForState : playerName,
        turn_count: Object.keys(colorsToUse).length,
        current_player_index: currentTurn === 'player1' ? 1 : 2,
        player1_node: graphData.player1_node,
        player2_node: graphData.player2_node,
      };

      const graphIdForCall = isChallengeModeRef.current ? CHALLENGE_GRAPH_ID : parseInt(selectedGraphId);

      const response = await fetch(`${API_BASE_URL}/api/interim_adjudicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          game_state: gameState,
          graph_id: graphIdForCall,
          game_id: gameIdRef.current
        })
      });

      if (!response.ok) throw new Error('Failed to fetch influence vector');

      const influenceData = await response.json();
      const vector = Array.isArray(influenceData) ? influenceData : influenceData.correlation_difference_vector || [];
      setInfluenceVector(vector);
      setInterimScore(influenceData.interim_score || 0);
    } catch (error) {
      console.error('Error fetching influence vector:', error);
      setInfluenceVector([]);
    }
  };

  const handleColorSelect = (color) => {
    if (selectedEdge !== null) {
      handleEdgeColoring(selectedEdge, color, true);
    }
  };

  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const isAgentTurn = currentTurn !== selectedPlayer;
    if (!isAgentTurn) return;

    const delayMs = 800;
    const timer = setTimeout(() => {
      if (!gameStarted || gameOver) return;
      if (currentTurn === selectedPlayer) return;
      makeAgentMove();
    }, delayMs);

    return () => clearTimeout(timer);
  }, [currentTurn, gameStarted, gameOver, selectedPlayer, selectedAgent, graphData]);

  // --- D3 render: main draw (no hoveredEdge dependency) -----------------------
  // BUGFIX: original effect listed hoveredEdge in its dep array, which tore down
  // and rebuilt the entire SVG on every mouse-enter/leave. Hover is now handled
  // purely inside the D3 mouse handlers below.
  useEffect(() => {
    if (!graphData || !gameStarted) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const { edge_list, vertex_positions, player1_node, player2_node } = graphData;

    const xExtent = d3.extent(vertex_positions, d => d[0]);
    const yExtent = d3.extent(vertex_positions, d => d[1]);

    const margin = 80;
    const xScale = d3.scaleLinear().domain(xExtent).range([margin, width - margin]);
    const yScale = d3.scaleLinear().domain(yExtent).range([margin, height - margin - 50]);

    const nodes = vertex_positions.map((pos, i) => ({
      id: i,
      x: xScale(pos[0]),
      y: yScale(pos[1]),
      isPlayer1: i === player1_node,
      isPlayer2: i === player2_node,
      influence: influenceVector[i] || 0
    }));

    const edges = edge_list.map(([source, target], index) => ({
      source: nodes[source],
      target: nodes[target],
      index: index,
      color: edgeColors[index] || 'uncolored'
    }));

    const defs = svg.append("defs");

    const redGlow = defs.append("filter")
      .attr("id", "red-glow")
      .attr("x", "-50%").attr("y", "-50%")
      .attr("width", "200%").attr("height", "200%");
    redGlow.append("feGaussianBlur").attr("stdDeviation", "4").attr("result", "coloredBlur");
    const redMerge = redGlow.append("feMerge");
    redMerge.append("feMergeNode").attr("in", "coloredBlur");
    redMerge.append("feMergeNode").attr("in", "SourceGraphic");

    const blueGlow = defs.append("filter")
      .attr("id", "blue-glow")
      .attr("x", "-50%").attr("y", "-50%")
      .attr("width", "200%").attr("height", "200%");
    blueGlow.append("feGaussianBlur").attr("stdDeviation", "4").attr("result", "coloredBlur");
    const blueMerge = blueGlow.append("feMerge");
    blueMerge.append("feMergeNode").attr("in", "coloredBlur");
    blueMerge.append("feMergeNode").attr("in", "SourceGraphic");

    svg.append("g")
      .attr("class", "edges")
      .selectAll("line")
      .data(edges)
      .enter()
      .append("line")
      .attr("x1", d => d.source.x)
      .attr("y1", d => d.source.y)
      .attr("x2", d => d.target.x)
      .attr("y2", d => d.target.y)
      .attr("stroke", d => {
        if (d.color === 'grey') return '#9ca3af';
        if (d.color === 'green') return '#10b981';
        if (d.color === 'purple') return '#a855f7';
        return '#e5e7eb';
      })
      .attr("stroke-width", 4)
      .attr("stroke-opacity", d => d.color === 'uncolored' ? 0.3 : 0.8)
      .style("cursor", d => d.color === 'uncolored' && currentTurn === selectedPlayer ? "pointer" : "default")
      .on("mouseenter", function(event, d) {
        if (d.color === 'uncolored' && currentTurn === selectedPlayer) {
          setHoveredEdge(d.index);
          d3.select(this).attr("stroke-width", 6).attr("stroke", "#fbbf24");
        }
      })
      .on("mouseleave", function(event, d) {
        if (d.color === 'uncolored') {
          setHoveredEdge(null);
          d3.select(this).attr("stroke-width", 4).attr("stroke", "#e5e7eb");
        }
      })
      .on("click", function(event, d) {
        if (d.color === 'uncolored' && currentTurn === selectedPlayer && !gameOver) {
          setSelectedEdge(d.index);
          const menuHeight = 220;
          const menuWidth = 150;
          const padding = 10;

          let x = event.clientX;
          let y = event.clientY - menuHeight - padding;
          if (y < padding) y = event.clientY + padding;
          if (y + menuHeight > window.innerHeight - padding) y = event.clientY - menuHeight - padding;
          if (x + menuWidth > window.innerWidth - padding) x = window.innerWidth - menuWidth - padding;
          if (x < padding) x = padding;

          setColorMenuPosition({ x: x, y: y });
          setShowColorMenu(true);
        }
      });

    const nodeGroup = svg.append("g").attr("class", "nodes");

    const getGlowColor = (influence) => {
      if (influence === 0) return null;
      return influence > 0 ? '#ef4444' : '#3b82f6';
    };

    const getGlowFilter = (influence) => {
      if (influence === 0) return null;
      return influence > 0 ? 'url(#red-glow)' : 'url(#blue-glow)';
    };

    nodeGroup.selectAll("circle.glow")
      .data(nodes)
      .enter()
      .append("circle")
      .attr("class", "glow")
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", d => {
        const baseRadius = (d.isPlayer1 || d.isPlayer2) ? 15 : 10;
        const absInfluence = Math.abs(d.influence);
        if (absInfluence === 0) return baseRadius;
        const glowScale = Math.sqrt(absInfluence) * 25 + 5;
        return baseRadius + glowScale;
      })
      .attr("fill", d => d.influence === 0 ? "none" : getGlowColor(d.influence))
      .attr("opacity", d => {
        if (d.influence === 0) return 0;
        const absInfluence = Math.abs(d.influence);
        const minOpacity = 0.15;
        const maxOpacity = 0.6;
        const scaledOpacity = Math.sqrt(absInfluence) * 0.5;
        return Math.min(maxOpacity, minOpacity + scaledOpacity);
      })
      .attr("filter", d => d.influence === 0 ? null : getGlowFilter(d.influence))
      .attr("pointer-events", "none");

    nodeGroup.selectAll("circle.node")
      .data(nodes)
      .enter()
      .append("circle")
      .attr("class", "node")
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", d => (d.isPlayer1 || d.isPlayer2) ? 15 : 10)
      .attr("fill", d => {
        if (d.isPlayer1) return "#ef4444";
        if (d.isPlayer2) return "#3b82f6";
        return "#6b7280";
      })
      .attr("stroke", "#fff")
      .attr("stroke-width", 2);

    svg.append("g")
      .attr("class", "labels")
      .selectAll("text")
      .data(nodes.filter(n => n.isPlayer1 || n.isPlayer2))
      .enter()
      .append("text")
      .attr("x", d => d.x)
      .attr("y", d => d.y - 25)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("font-weight", "bold")
      .attr("fill", d => d.isPlayer1 ? "#ef4444" : "#3b82f6")
      .text(d => d.isPlayer1 ? "Player 1" : "Player 2");

    svg.append("g")
      .attr("class", "node-indices")
      .selectAll("text")
      .data(nodes)
      .enter()
      .append("text")
      .attr("x", d => d.x)
      .attr("y", d => d.y + 4)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("fill", "white")
      .attr("pointer-events", "none")
      .text(d => d.id);

  }, [graphData, edgeColors, gameStarted, currentTurn, selectedPlayer, gameOver, width, height, influenceVector]);
  // (hoveredEdge intentionally NOT in deps — see BUGFIX comment above.)

  const getWinnerText = (gameWinner) => {
    if (gameWinner === 'draw') return "It's a Draw!";
    const opponentName = isChallengeMode ? CHALLENGE_AGENT : selectedAgent;
    if (gameWinner === 'red') {
      const humanIsRed = selectedPlayer === 'player1';
      return `Player 1 (Red)${humanIsRed ? ' (You!)' : ` (${opponentName})`}`;
    }
    if (gameWinner === 'blue') {
      const humanIsBlue = selectedPlayer === 'player2';
      return `Player 2 (Blue)${humanIsBlue ? ' (You!)' : ` (${opponentName})`}`;
    }
    return 'Unknown';
  };

  // Compact ScoreBar: no outer padding/background — it's now embedded inside
  // the combined header row to save vertical space.
  const ScoreBar = () => {
    const barWidth = 320;
    const barHeight = 28;
    const currentScore = interimScore || 0;

    const clampedScore = Math.max(-MAX_SCORE_DISPLAY, Math.min(MAX_SCORE_DISPLAY, currentScore));
    const scorePercentage = (clampedScore / MAX_SCORE_DISPLAY) * 50;
    const epsilonPercentage = (epsilon / MAX_SCORE_DISPLAY) * 50;

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '5px'
      }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
          Current Score: <span style={{
            fontWeight: 'bold',
            color: currentScore > 0 ? '#ef4444' : currentScore < 0 ? '#3b82f6' : '#6b7280'
          }}>
            {currentScore.toFixed(3)}
          </span>
        </div>

        <div style={{
          position: 'relative',
          width: `${barWidth}px`,
          height: `${barHeight}px`,
          backgroundColor: '#e5e7eb',
          borderRadius: '6px',
          overflow: 'hidden',
          border: '2px solid #9ca3af'
        }}>
          {currentScore < 0 && (
            <div style={{
              position: 'absolute', left: '50%', top: 0,
              width: `${Math.abs(scorePercentage)}%`, height: '100%',
              backgroundColor: '#3b82f6', transition: 'width 0.3s ease'
            }} />
          )}
          {currentScore > 0 && (
            <div style={{
              position: 'absolute', right: '50%', top: 0,
              width: `${scorePercentage}%`, height: '100%',
              backgroundColor: '#ef4444', transition: 'width 0.3s ease'
            }} />
          )}
          <div style={{
            position: 'absolute', left: '50%', top: 0,
            width: '3px', height: '100%', backgroundColor: '#1f2937',
            transform: 'translateX(-50%)', zIndex: 2
          }} />
          <div style={{
            position: 'absolute', left: `${50 + epsilonPercentage}%`, top: 0,
            width: '2px', height: '100%', backgroundColor: '#059669',
            transform: 'translateX(-50%)', zIndex: 1
          }} />
          <div style={{
            position: 'absolute', left: `${50 - epsilonPercentage}%`, top: 0,
            width: '2px', height: '100%', backgroundColor: '#059669',
            transform: 'translateX(-50%)', zIndex: 1
          }} />
          <div style={{
            position: 'absolute', left: '5px', top: '50%',
            transform: 'translateY(-50%)', fontSize: '11px',
            fontWeight: 'bold', color: '#1f2937', zIndex: 3
          }}>Red</div>
          <div style={{
            position: 'absolute', right: '5px', top: '50%',
            transform: 'translateY(-50%)', fontSize: '11px',
            fontWeight: 'bold', color: '#1f2937', zIndex: 3
          }}>Blue</div>
        </div>

        <div style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center' }}>
          Draw at ±{epsilon} (green) · Range ±{MAX_SCORE_DISPLAY}
        </div>
      </div>
    );
  };

  // === Challenge UI fragments ================================================
  const ChallengeProgressBanner = () => {
    if (!isChallengeMode) return null;
    const tally = tallyChallenge(challengeResults);

    return (
      <div style={{
        background: 'linear-gradient(90deg, #78350f, #b45309, #78350f)',
        color: '#fef3c7',
        padding: '14px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '3px solid #fbbf24',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '22px' }}>💰</span>
          <div>
            <div style={{ fontWeight: 'bold', letterSpacing: '1px', fontSize: '15px' }}>
              CHALLENGE
            </div>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>
              Game {challengeGameNum} of {CHALLENGE_TOTAL_GAMES} · You are {selectedPlayer === 'player1' ? 'Red' : 'Blue'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {[1, 2, 3, 4, 5].map(n => {
            const entry = challengeResults.find(r => r.gameNum === n);
            let bg = 'rgba(255,255,255,0.12)';
            let symbol = n;
            if (entry?.result === 'win')  { bg = '#10b981'; symbol = 'W'; }
            if (entry?.result === 'loss') { bg = '#ef4444'; symbol = 'L'; }
            if (entry?.result === 'draw') { bg = '#6b7280'; symbol = 'D'; }
            if (!entry && n === challengeGameNum) bg = '#fbbf24';
            return (
              <div key={n} style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: bg,
                color: entry || n === challengeGameNum ? '#1f2937' : '#fef3c7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 'bold', fontSize: '14px',
                border: n === challengeGameNum && !entry ? '2px solid #fff' : '2px solid transparent'
              }}>
                {symbol}
              </div>
            );
          })}
          <div style={{ marginLeft: '12px', fontSize: '13px' }}>
            <span style={{ color: '#86efac', fontWeight: 'bold' }}>{tally.wins}W</span>
            {' · '}
            <span style={{ color: '#fca5a5', fontWeight: 'bold' }}>{tally.losses}L</span>
            {' · '}
            <span style={{ color: '#d1d5db', fontWeight: 'bold' }}>{tally.draws}D</span>
          </div>
        </div>

        <button
          onClick={handleForfeitChallenge}
          style={{
            padding: '6px 14px',
            background: 'rgba(0,0,0,0.3)',
            color: '#fef3c7',
            border: '1px solid #fef3c7',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold'
          }}
        >
          Forfeit
        </button>
      </div>
    );
  };
  // ===========================================================================

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  // If user is logged in but hasn't set a username, show prompt to go to Dashboard
  if (needsUsername) {
    return (
      <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '30px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>👤</div>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '15px', color: '#92400e' }}>
            Username Required
          </h2>
          <p style={{ fontSize: '16px', color: '#78350f', marginBottom: '25px', lineHeight: '1.6' }}>
            Before you can play, you need to set up your username.
            Please visit the Dashboard to create your unique player name.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '14px 28px',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#d97706'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#f59e0b'}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // SELECTION SCREEN
  // ===========================================================================
  if (!gameStarted) {
    return (
      <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
        {/* Keyframes for the challenge button */}
        <style>{`
          @keyframes challengePulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.7),
                          0 6px 24px rgba(180, 83, 9, 0.5),
                          inset 0 1px 0 rgba(255,255,255,0.4);
            }
            50% {
              box-shadow: 0 0 0 16px rgba(251, 191, 36, 0),
                          0 8px 32px rgba(180, 83, 9, 0.7),
                          inset 0 1px 0 rgba(255,255,255,0.4);
            }
          }
          @keyframes challengeShimmer {
            0% { transform: translateX(-120%) skewX(-20deg); }
            100% { transform: translateX(220%) skewX(-20deg); }
          }
          .challenge-button {
            animation: challengePulse 2.2s ease-in-out infinite;
          }
          .challenge-button:hover { transform: translateY(-2px); }
          .challenge-button:active { transform: translateY(1px); }
          .challenge-shimmer {
            position: absolute;
            top: 0; left: 0;
            width: 40%; height: 100%;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255, 255, 255, 0.45),
              transparent
            );
            animation: challengeShimmer 3s ease-in-out infinite;
            pointer-events: none;
          }
        `}</style>

        <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center' }}>
          Game Selection
        </h1>

        {(graphsLoading || isLoading || challengeStarting) && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
            {challengeStarting ? 'Starting the Challenge...' : 'Loading...'}
          </div>
        )}

        {!graphsLoading && !isLoading && !challengeStarting && (
          <>
            {/* ------------------------------------------------------------ */}
            {/* CHALLENGE BUTTON                                    */}
            {/* ------------------------------------------------------------ */}
            <button
              className="challenge-button"
              onClick={startChallenge}
              disabled={challengeStarting}
              style={{
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
                padding: '22px 24px',
                background: 'linear-gradient(135deg, #fcd34d 0%, #f59e0b 45%, #b45309 100%)',
                color: '#1f2937',
                border: '3px solid #78350f',
                borderRadius: '14px',
                fontSize: '26px',
                fontWeight: '900',
                letterSpacing: '2px',
                cursor: 'pointer',
                marginBottom: '14px',
                textShadow: '0 1px 0 rgba(255,255,255,0.4)',
                transition: 'transform 0.15s ease'
              }}
            >
              <div className="challenge-shimmer" />
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '14px'
              }}>
                <span style={{ fontSize: '34px', filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.25))' }}>🏆</span>
                <span>CHALLENGE</span>
                <span style={{ fontSize: '34px', filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.25))' }}>🏆</span>
              </div>
              <div style={{
                position: 'relative',
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '1px',
                marginTop: '6px',
                color: '#451a03'
              }}>
                BEST OF 5 · PETERSEN GRAPH · VS ALPHAQ UP
              </div>
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '20px 0 26px 0',
              color: '#9ca3af',
              fontSize: '13px',
              fontWeight: '600',
              letterSpacing: '2px'
            }}>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
              <span>OR PICK YOUR OWN MATCH</span>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Select Your Player:
              </label>
              <div style={{ display: 'flex', gap: '15px' }}>
                <button
                  onClick={() => setSelectedPlayer('player1')}
                  style={{
                    flex: 1, padding: '12px',
                    border: '2px solid',
                    borderColor: selectedPlayer === 'player1' ? '#ef4444' : '#e5e7eb',
                    backgroundColor: selectedPlayer === 'player1' ? '#fee2e2' : 'white',
                    borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
                  }}
                >
                  Player 1 (Red)
                </button>
                <button
                  onClick={() => setSelectedPlayer('player2')}
                  style={{
                    flex: 1, padding: '12px',
                    border: '2px solid',
                    borderColor: selectedPlayer === 'player2' ? '#3b82f6' : '#e5e7eb',
                    backgroundColor: selectedPlayer === 'player2' ? '#dbeafe' : 'white',
                    borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
                  }}
                >
                  Player 2 (Blue)
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Select Game Graph:
              </label>
              <select
                value={selectedGraphId}
                onChange={(e) => setSelectedGraphId(e.target.value)}
                disabled={availableGraphIds.length === 0}
                style={{
                  width: '100%', padding: '10px',
                  border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px'
                }}
              >
                <option value="">
                  {availableGraphIds.length === 0 ? "No graphs available" : "Choose a graph..."}
                </option>
                {Array.from(graphNameMap.keys())
                  .filter(graphId => availableGraphIds.includes(graphId))
                  .filter(graphId => {
                    const isLargeGraph = graphId === 5 || graphId === 12;
                    if (selectedAgent === 'AlphaZero Andy' && isLargeGraph) return false;
                    if (selectedAgent === 'AlphaZero Amara' && !isLargeGraph) return false;
                    return true;
                  })
                  .map(graphId => (
                    <option key={graphId} value={graphId}>
                      {graphNameMap.get(graphId)}
                    </option>
                  ))}
              </select>
              {availableGraphIds.length === 0 && (
                <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
                  Unable to load graphs. Please check your connection.
                </p>
              )}
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Select Opponent:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {['Random Randy', 'MCTS Murray', 'MCTS Melissa', 'AlphaZero Andy', 'AlphaQ Up'].map(agent => {
                  const graphId = selectedGraphId ? parseInt(selectedGraphId) : null;
                  const isLargeGraph = graphId === 5 || graphId === 12;
                  let isDisabled = false;
                  if (agent === 'AlphaZero Andy' && isLargeGraph) isDisabled = true;
                  if (agent === 'AlphaZero Amara' && graphId && !isLargeGraph) isDisabled = true;

                  return (
                    <button
                      key={agent}
                      onClick={() => setSelectedAgent(agent)}
                      disabled={isDisabled}
                      style={{
                        padding: '12px',
                        border: '2px solid',
                        borderColor: selectedAgent === agent ? '#10b981' : '#e5e7eb',
                        backgroundColor: selectedAgent === agent ? '#d1fae5' : 'white',
                        borderRadius: '8px', fontWeight: '500',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.5 : 1
                      }}
                    >
                      {agent}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              onClick={handleStartGame}
              disabled={!selectedPlayer || !selectedGraphId || !selectedAgent || isLoading}
              style={{
                width: '100%', padding: '14px',
                backgroundColor: (!selectedPlayer || !selectedGraphId || !selectedAgent) ? '#9ca3af' : '#10b981',
                color: 'white', border: 'none', borderRadius: '8px',
                fontSize: '18px', fontWeight: 'bold',
                cursor: (!selectedPlayer || !selectedGraphId || !selectedAgent || isLoading) ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Starting Game...' : 'Start Game'}
            </button>
          </>
        )}
      </div>
    );
  }

  // ===========================================================================
  // GAME SCREEN
  // ===========================================================================
  const challengeTally = tallyChallenge(challengeResults);

  return (
    <div style={{ position: 'relative' }}>
      <ChallengeProgressBanner />

      <div style={{
        display: 'flex',
        padding: '14px 20px',
        backgroundColor: '#f3f4f6',
        borderBottom: '2px solid #e5e7eb',
        gap: '24px',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
      }}>
        {/* Left: title + game info */}
        <div style={{ flex: '1 1 280px', minWidth: '240px', textAlign: 'left' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 4px 0'
          }}>
            Tangled on the {graphNameMap.get(parseInt(selectedGraphId))}
          </h2>

          <p style={{ color: '#6b7280', fontSize: '13px', margin: '4px 0' }}>
            You are: <span style={{
              fontWeight: 'bold',
              color: selectedPlayer === 'player1' ? '#ef4444' : '#3b82f6'
            }}>
              {selectedPlayer === 'player1' ? 'Player 1 (Red)' : 'Player 2 (Blue)'}
            </span> |
            Opponent: <span style={{ fontWeight: 'bold' }}>{isChallengeMode ? CHALLENGE_AGENT : selectedAgent}</span>
          </p>

          <p style={{ fontSize: '15px', fontWeight: '600', margin: '4px 0' }}>
            Current Turn: <span style={{
              color: currentTurn === 'player1' ? '#ef4444' : '#3b82f6'
            }}>
              {currentTurn === 'player1' ? 'Player 1 (Red)' : 'Player 2 (Blue)'}
              {currentTurn === selectedPlayer && ' (Your Turn)'}
            </span>
          </p>

          <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>
            Edges colored: {Object.keys(edgeColors).length}/{graphData.edge_list.length}
          </p>
        </div>

        {/* Right: compact score bar */}
        <div style={{ flexShrink: 0 }}>
          <ScoreBar />
        </div>
      </div>

      <svg ref={svgRef}></svg>

      {showColorMenu && (
        <div style={{
          position: 'fixed',
          left: colorMenuPosition.x + 'px',
          top: colorMenuPosition.y + 'px',
          backgroundColor: 'white',
          border: '2px solid #374151',
          borderRadius: '8px',
          padding: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1000
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
            Select Color for Edge {selectedEdge}:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button onClick={() => handleColorSelect('grey')} style={{
              padding: '8px 16px', backgroundColor: '#9ca3af', color: 'white',
              border: 'none', borderRadius: '4px', cursor: 'pointer'
            }}>Grey</button>
            <button onClick={() => handleColorSelect('green')} style={{
              padding: '8px 16px', backgroundColor: '#10b981', color: 'white',
              border: 'none', borderRadius: '4px', cursor: 'pointer'
            }}>Green</button>
            <button onClick={() => handleColorSelect('purple')} style={{
              padding: '8px 16px', backgroundColor: '#a855f7', color: 'white',
              border: 'none', borderRadius: '4px', cursor: 'pointer'
            }}>Purple</button>
            <button onClick={() => { setShowColorMenu(false); setSelectedEdge(null); }} style={{
              marginTop: '4px', padding: '8px 16px', backgroundColor: '#ef4444',
              color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Normal game-over modal (only when NOT in challenge mode) */}
      {gameOver && !isChallengeMode && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white', padding: '40px',
            borderRadius: '12px', textAlign: 'center'
          }}>
            <h2 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '20px' }}>
              Game Over!
            </h2>
            <p style={{ fontSize: '24px', marginBottom: '30px' }}>
              Winner: <span style={{
                fontWeight: 'bold',
                color: winner === 'red' ? '#ef4444' : winner === 'blue' ? '#3b82f6' : '#6b7280'
              }}>
                {getWinnerText(winner)}
              </span>
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px', backgroundColor: '#10b981', color: 'white',
                border: 'none', borderRadius: '8px',
                fontSize: '18px', fontWeight: 'bold', cursor: 'pointer'
              }}
            >
              Play Again
            </button>
          </div>
        </div>
      )}

      {/* Challenge inter-game transition */}
      {isChallengeMode && showGameTransition && !showChallengeComplete && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #fcd34d, #b45309)',
            padding: '36px 56px',
            borderRadius: '16px',
            textAlign: 'center',
            border: '3px solid #78350f',
            color: '#1f2937'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', letterSpacing: '2px', opacity: 0.8 }}>
              GAME {challengeGameNum} RESULT
            </div>
            <div style={{
              fontSize: '46px', fontWeight: 900, margin: '8px 0 12px 0',
              color: lastGameResult === 'win' ? '#065f46'
                : lastGameResult === 'loss' ? '#7f1d1d'
                : '#1f2937'
            }}>
              {lastGameResult === 'win' ? 'YOU WON!' : lastGameResult === 'loss' ? 'YOU LOST' : 'DRAW'}
            </div>
            <div style={{ fontSize: '16px', marginBottom: '14px' }}>
              Series: <b style={{ color: '#065f46' }}>{challengeTally.wins}W</b>
              {' · '}
              <b style={{ color: '#7f1d1d' }}>{challengeTally.losses}L</b>
              {' · '}
              <b>{challengeTally.draws}D</b>
            </div>
            <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
              {challengeGameNum < CHALLENGE_TOTAL_GAMES
                ? `Game ${challengeGameNum + 1} starts in a moment — you'll be ${(challengeGameNum + 1) % 2 === 1 ? 'Red' : 'Blue'}.`
                : 'Tallying final results...'}
            </div>
          </div>
        </div>
      )}

      {/* Challenge complete modal */}
      {showChallengeComplete && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 2100,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '560px',
            width: '100%',
            overflow: 'hidden',
            border: '3px solid #78350f',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            {/* Header */}
            <div style={{
              background: challengeTally.overall === 'player_won'
                ? 'linear-gradient(135deg, #fcd34d, #b45309)'
                : challengeTally.overall === 'ai_won'
                ? 'linear-gradient(135deg, #6b7280, #1f2937)'
                : 'linear-gradient(135deg, #9ca3af, #4b5563)',
              color: '#fff',
              padding: '30px 24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', letterSpacing: '3px', fontWeight: 'bold', opacity: 0.9 }}>
                CHALLENGE
              </div>
              <div style={{ fontSize: '36px', fontWeight: 900, margin: '12px 0' }}>
                {challengeTally.overall === 'player_won' && '🏆 YOU WIN THE CHALLENGE!'}
                {challengeTally.overall === 'ai_won' && '😔 ALPHAQ UP WINS'}
                {challengeTally.overall === 'draw' && '🤝 SERIES DRAWN'}
              </div>
              <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
                You {challengeTally.wins} · {challengeTally.losses} AlphaQ Up
                {challengeTally.draws > 0 ? ` · ${challengeTally.draws} draw${challengeTally.draws === 1 ? '' : 's'}` : ''}
              </div>
            </div>

            {/* Game-by-game */}
            <div style={{ padding: '24px' }}>
              <div style={{
                fontSize: '13px', fontWeight: 'bold',
                color: '#6b7280', letterSpacing: '1px', marginBottom: '12px'
              }}>
                GAME-BY-GAME
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[1, 2, 3, 4, 5].map(n => {
                  const entry = challengeResults.find(r => r.gameNum === n);
                  const playerColor = n % 2 === 1 ? 'Red' : 'Blue';
                  if (!entry) {
                    return (
                      <div key={n} style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '10px 14px', background: '#f3f4f6',
                        borderRadius: '8px', color: '#9ca3af', fontSize: '14px'
                      }}>
                        <span>Game {n} ({playerColor}) — not played</span>
                        <span style={{ fontWeight: 'bold' }}>FORFEIT</span>
                      </div>
                    );
                  }
                  const color = entry.result === 'win' ? '#10b981'
                    : entry.result === 'loss' ? '#ef4444' : '#6b7280';
                  return (
                    <div key={n} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px',
                      background: entry.result === 'win' ? '#ecfdf5' : entry.result === 'loss' ? '#fef2f2' : '#f3f4f6',
                      borderLeft: `4px solid ${color}`,
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <span>
                        <b>Game {n}</b> ({playerColor}) — {entry.result.toUpperCase()}
                      </span>
                      {entry.eloChange !== null && entry.eloChange !== undefined && (
                        <span style={{ color: '#374151', fontFamily: 'monospace' }}>
                          ELO {entry.eloBefore ?? '?'} → {entry.eloAfter ?? '?'}
                          {' '}
                          <b style={{ color: entry.eloChange >= 0 ? '#10b981' : '#ef4444' }}>
                            ({entry.eloChange >= 0 ? '+' : ''}{entry.eloChange})
                          </b>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => window.location.reload()}
                style={{
                  width: '100%', marginTop: '24px',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #10b981, #047857)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '18px', fontWeight: 'bold', cursor: 'pointer'
                }}
              >
                Return to Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GraphGame;